import type {
  Camera,
  Intersection,
  QuaternionTuple,
  Vector3,
  Vector3Like,
  Vector3Tuple,
} from 'three';
import { Quaternion, Vector2 } from 'three';
import type { OrbitControls } from 'three-stdlib';

import { TOOL_CONST } from '@/constant';
import {
  type NEW__LabelingBox,
  _createSubscriber,
  getVectorByAxis,
} from '@/util';

import { getIntersectObjectsByNDC, getIntersectPlanePointByNDC } from '.';

export const setControlEnabled = (
  nextState: boolean,
  controls?: OrbitControls,
) => {
  if (!controls) return;

  controls.enabled = nextState;
  controls.enablePan = nextState;
  controls.enableRotate = nextState;

  controls.update();
};

export const isEnabled = (disabled: boolean) => !disabled;

export const isTopMode = (mode: string) =>
  mode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP;

export const isSideMode = (mode: string) =>
  mode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE;

export const isFrontMode = (mode: string) =>
  mode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT;

export const isOtherOperationRunning = (isOtherOperationRunning: boolean) =>
  isOtherOperationRunning;

export const validMouseEventAndCanvasElement = (
  e: MouseEvent,
  button: MouseEvent['button'] = 0,
) =>
  e.button === button &&
  !e.ctrlKey &&
  !e.metaKey &&
  e.target instanceof HTMLCanvasElement &&
  !!e.target.closest("[data-view-text='default']");

export const withIntersectObject =
  (camera: Camera, labelingBoxList: NEW__LabelingBox[]) =>
  (e: MouseEventWith<{ canvasX: number; canvasY: number }>) => {
    const lineIntersections = getIntersectObjectsByNDC({
      ndc: new Vector2(e.canvasX, e.canvasY),
      camera,
      objects: labelingBoxList.map(v => v.line2),
    });

    if (lineIntersections.length > 0) {
      return { event: e, intersections: [] };
    }

    const boxIntersections = getIntersectObjectsByNDC({
      ndc: new Vector2(e.canvasX, e.canvasY),
      camera,
      objects: labelingBoxList.map(v => v.boxMesh),
    });

    return { event: e, intersections: boxIntersections };
  };

export const isIntersected = (intersections: Intersection[]) =>
  !!intersections.length;

export const isSelectedLabelingBoxList = (
  labelingBoxList: NEW__LabelingBox[],
) => !!labelingBoxList.length;

export const withIntersectPlanePoint = ({
  event,
  camera,
  coplanarPoint,
}: {
  event: MouseEventWith<{ canvasX: number; canvasY: number }>;
  camera: Camera;
  coplanarPoint: Vector3;
}) => {
  const interesectPlanePoint = getIntersectPlanePointByNDC({
    ndc: new Vector2(event.canvasX, event.canvasY),
    camera,
    coplanarPoint,
  });

  return interesectPlanePoint;
};

export const applyMoveOffsetToBox =
  (moveOffset: Vector3, orgPostion: Vector3 | Vector3Like) =>
  (boxID: string) => {
    return [
      boxID,
      [
        orgPostion.x + moveOffset.x,
        orgPostion.y + moveOffset.y,
        orgPostion.z + moveOffset.z,
      ],
    ] as [string, Vector3Tuple];
  };

export const isFirstIntersectedBoxEqaul = (
  ...intersectionsList: Intersection[][]
) => {
  return intersectionsList.every(
    intersections =>
      intersections.at(0)?.object.userData.id ===
      intersectionsList.at(0)?.at(0)?.object.userData.id,
  );
};

export const getAngleRadBetweenTwoPoints = (
  originalPoint: Vector3 | Vector3Like,
  targetPoint: Vector3 | Vector3Like,
) => {
  return Math.atan2(
    targetPoint.y - originalPoint.y,
    targetPoint.x - originalPoint.x,
  );
};

export const rotateQuaternionByAxis = (
  quaternion: Quaternion,
  { angleRad, axis }: { angleRad: number; axis: 'x' | 'y' | 'z' },
) => {
  const axisQuaternion = new Quaternion().setFromAxisAngle(
    getVectorByAxis(axis),
    angleRad,
  );
  return axisQuaternion.multiply(quaternion);
};

export const applyAngleRadToBox =
  (angleRad: number, axis: 'x' | 'y' | 'z') => (box: NEW__LabelingBox) => {
    const axisQuaternion = rotateQuaternionByAxis(box.boxMesh.quaternion, {
      angleRad,
      axis,
    });

    return [box.id, axisQuaternion.toArray()] as [string, QuaternionTuple];
  };
